import { doc, getFirestore, runTransaction } from "firebase/firestore";
import { type ChangeEvent, useCallback, useState } from "react";
import { useAuthState, useRutasActions } from "../../../context/AppContext";
import {
  type ActualizacionRutaMovimiento,
  aplicarMovimientoARutaLocal,
  construirActualizacionRutaConMovimiento,
  crearMovimientoRutaData,
  crearMovimientoRutaRef,
} from "../../../utils/rutaMovimientos";
import type {
  ModalEditRutaProps,
  OperacionSaldo,
  RutaFieldKey,
  UseModalEditRutaReturn,
} from "./types";

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const normalizarTexto = (value: string | null | undefined): string => (value ?? "").trim();

export const useModalEditRuta = ({
  selectedRuta,
  setShowModalEdit,
}: ModalEditRutaProps): UseModalEditRutaReturn | null => {
  const [responsable, setResponsable] = useState(selectedRuta?.responsable ?? "");
  const [valor, setValor] = useState(0);
  const [sumaResta, setSumaResta] = useState<OperacionSaldo>("");
  const [isEditing, setIsEditing] = useState(false);
  const { setRutasData } = useRutasActions();
  const { userData } = useAuthState();

  const adminUid = selectedRuta?.adminUid ?? userData?.uid ?? null;
  const rutaUid = selectedRuta?.uid ?? null;
  const responsableActual = normalizarTexto(selectedRuta?.responsable);
  const responsableEditado = normalizarTexto(responsable);
  const hasResponsableChange = responsableEditado !== responsableActual;
  const hasBalanceOperation = sumaResta !== "" && valor > 0;
  const canSubmit = hasResponsableChange || hasBalanceOperation;

  const handleClose = useCallback(() => {
    setShowModalEdit(false);
  }, [setShowModalEdit]);

  const handleFieldChange = useCallback(
    (field: RutaFieldKey) => (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (field === "responsable") {
        setResponsable(value);
        return;
      }

      setValor(normalizarNumero(value));
    },
    [],
  );

  const handleOperacionChange = useCallback((operacion: OperacionSaldo) => {
    setSumaResta(operacion);
  }, []);

  const editarRuta = useCallback(async () => {
    if (isEditing || !adminUid || !rutaUid || !canSubmit) {
      return;
    }

    setIsEditing(true);

    try {
      const db = getFirestore();
      const rutaRef = doc(db, "admin_users", adminUid, "rutas", rutaUid);
      const movimientoRef = hasBalanceOperation
        ? crearMovimientoRutaRef(db, adminUid, rutaUid)
        : null;
      const fechaMovimiento = new Date();

      const resultadoTransaccion = await runTransaction(db, async (transaction) => {
        const rutaSnapshot = await transaction.get(rutaRef);
        const rutaData = rutaSnapshot.data();

        if (!rutaData) {
          throw new Error("No se pudo obtener la ruta actual.");
        }

        const saldoActualRuta = normalizarNumero(rutaData.saldoInicial);
        const historialSaldosActual = normalizarNumero(rutaData.historialSaldos);
        let nuevoMonto = saldoActualRuta;
        let nuevoHistorial = historialSaldosActual;
        let movimientoCreado: ReturnType<typeof crearMovimientoRutaData> | null = null;
        let rutaUpdates: {
          historialSaldos: number;
          responsable: string;
          saldoInicial: number;
          movimientosCount?: ActualizacionRutaMovimiento["movimientosCount"];
          totalIngresos?: ActualizacionRutaMovimiento["totalIngresos"];
          totalSalidas?: ActualizacionRutaMovimiento["totalSalidas"];
          ultimoMovimientoAt?: ActualizacionRutaMovimiento["ultimoMovimientoAt"];
        } = {
          historialSaldos: nuevoHistorial,
          responsable: responsableEditado,
          saldoInicial: nuevoMonto,
        };

        if (hasBalanceOperation) {
          const esSuma = sumaResta === "sumar";
          const motivo = esSuma ? "Ingreso" : "Retiro";

          nuevoMonto = esSuma ? saldoActualRuta + valor : saldoActualRuta - valor;
          nuevoHistorial = esSuma ? historialSaldosActual + valor : historialSaldosActual - valor;
          movimientoCreado = crearMovimientoRutaData({
            descripcion: motivo,
            fecha: fechaMovimiento,
            monto: valor,
            origen: "editar_ruta",
            responsable: "Admin",
            saldoDespues: nuevoMonto,
          });
          rutaUpdates = {
            historialSaldos: nuevoHistorial,
            responsable: responsableEditado,
            ...construirActualizacionRutaConMovimiento(rutaData, movimientoCreado, nuevoMonto),
          };
        }

        if (movimientoRef && movimientoCreado) {
          transaction.set(movimientoRef, movimientoCreado);
        }

        transaction.update(rutaRef, rutaUpdates);

        return {
          movimientoCreado,
          nuevoHistorial,
          nuevoMonto,
        };
      });

      setRutasData((prevRutas) =>
        prevRutas
          ? prevRutas.map((ruta) =>
              ruta.uid === rutaUid
                ? resultadoTransaccion.movimientoCreado
                  ? {
                      ...aplicarMovimientoARutaLocal(
                        ruta,
                        resultadoTransaccion.movimientoCreado,
                        resultadoTransaccion.nuevoMonto,
                      ),
                      historialSaldos: resultadoTransaccion.nuevoHistorial,
                      responsable: responsableEditado,
                    }
                  : {
                      ...ruta,
                      historialSaldos: resultadoTransaccion.nuevoHistorial,
                      responsable: responsableEditado,
                      saldoInicial: resultadoTransaccion.nuevoMonto,
                    }
                : ruta,
            )
          : prevRutas,
      );
      handleClose();
    } finally {
      setIsEditing(false);
    }
  }, [
    adminUid,
    canSubmit,
    handleClose,
    hasBalanceOperation,
    isEditing,
    responsableEditado,
    rutaUid,
    setRutasData,
    sumaResta,
    valor,
  ]);

  if (!selectedRuta) {
    return null;
  }

  return {
    canSubmit,
    editarRuta,
    handleClose,
    handleFieldChange,
    handleOperacionChange,
    isEditing,
    responsable,
    selectedRuta,
    sumaResta,
    valor,
  };
};
